import { Col, message, Row, Space, Spin, Tag } from "antd";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import React, { Component } from "react";
import { connect } from "react-redux";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { ResponsivePie } from "@nivo/pie";
import { ButtonItem, ErrorItem, Form, TableItemDinamico } from "../../../../Components/Items";
import FiltrosCalidad from "./FiltrosCalidad";
import {
  barrasPdf,
  descargarPdfCalidad,
  exportarExcel,
  fmtDuracion,
  fmtEjecutado,
  fmtFecha,
  fmtFraccion,
  fmtFraccionPct,
  fmtResultado,
} from "./exportCalidad";
import {
  ChartCard,
  COLOR_RESULTADO,
  COLORES_RESULTADO,
  fmtDiaCorto,
  KpiRow,
  NIVO_THEME,
  ordenarPorClave,
  RESULTADO_LABEL,
  RESULTADO_TAG,
  TooltipCalidad,
  isMobile,
} from "./reporteCalidadStyles";

const PERIODO_INICIAL = { periodo: [moment().subtract(1, "month"), moment()] };

class ReporteGeneral extends Component {
  formRef = React.createRef();
  tableRef = null;

  state = {
    loading: false,
    registros: [],
    totalRegistros: 0,
    resumen: null,
    empresa: null,
    currentFilters: {},
  };

  buildPayload(values = {}) {
    const payload = {};
    if (values.programaId) payload.programaId = values.programaId;
    if (values.procesoId) payload.procesoId = values.procesoId;
    if (values.resultado) payload.resultado = values.resultado;
    if (values.ejecutadoPor) payload.ejecutadoPor = values.ejecutadoPor;
    if (values.periodo?.length === 2) {
      payload.fechaInicio = values.periodo[0].startOf("day").toISOString();
      payload.fechaFin = values.periodo[1].endOf("day").toISOString();
    }
    return payload;
  }

  onFetchData(params) {
    this.setState({ loading: true });
    axios
      .post(
        "programa/reporteCalidad",
        { empresaId: this.props.empresaId, ...params },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            registros: res.data.data,
            totalRegistros: res.data.totalRecords,
            resumen: res.data.resumen,
            empresa: res.data.empresa ?? null,
          });
        } else {
          message.error("No se pudo generar el reporte");
        }
      })
      .catch(() => message.error("Error de conexión"))
      .finally(() => this.setState({ loading: false }));
  }

  consultarReporte(values) {
    const payload = this.buildPayload(values);
    this.setState({ currentFilters: payload });
    this.tableRef?.updateExternalFilters(payload);
  }

  async fetchAllRegistros() {
    const res = await axios.post(
      "programa/reporteCalidad",
      { empresaId: this.props.empresaId, ...this.state.currentFilters },
      { headers: this.props.headersToken }
    );
    if (res.data.success !== 2) throw new Error("Error al obtener datos");
    return res.data;
  }

  async descargarExcel() {
    this.setState({ loading: true });
    try {
      const { data: registros, resumen } = await this.fetchAllRegistros();
      const filas = registros.map((r) => ({
        Programa: r.programa?.nombre ?? "-",
        Proceso: r.proceso?.nombre ?? "-",
        "Ejecutado por": fmtEjecutado(r),
        Resultado: fmtResultado(r.resultado),
        "Fecha inicio": fmtFecha(r.fechaInicio),
        "Fecha fin": fmtFecha(r.fechaFin),
        "Duración (min)": r.duracionMinutos ?? "-",
        ...(resumen?.usaPuntos
          ? { "Puntos completados": fmtFraccion(r.puntosCompletados, r.puntosTotales) }
          : {}),
        ...(resumen?.usaPasos
          ? { "Pasos completados": fmtFraccion(r.pasosCompletados, r.pasosTotales) }
          : {}),
        ...(resumen?.usaCriterios
          ? { "Criterios cumplidos": fmtFraccion(r.criteriosCumplidos, r.criteriosTotales) }
          : {}),
        "% Cumplimiento": r.porcentajeCumplimiento != null ? `${r.porcentajeCumplimiento}%` : "-",
        Observaciones: r.observaciones || "",
      }));
      exportarExcel("Reporte_Calidad", "Reporte Calidad", filas);
    } catch {
      message.error("No se pudo generar el Excel");
    } finally {
      this.setState({ loading: false });
    }
  }

  async descargarPdf() {
    this.setState({ loading: true });
    try {
      const { data: registros, resumen, empresa } = await this.fetchAllRegistros();
      const { usaPuntos, usaPasos, usaCriterios } = resumen ?? {};

      const filaRegistro = (r) => [
        r.programa?.nombre ?? "-",
        r.proceso?.nombre ?? "-",
        fmtEjecutado(r),
        { text: fmtResultado(r.resultado), bold: true },
        fmtFecha(r.fechaInicio),
        fmtFecha(r.fechaFin),
        { text: fmtDuracion(r.duracionMinutos), alignment: "center" },
        ...(usaPuntos
          ? [{ text: fmtFraccion(r.puntosCompletados, r.puntosTotales), alignment: "center" }]
          : []),
        ...(usaPasos
          ? [{ text: fmtFraccionPct(r.pasosCompletados, r.pasosTotales), alignment: "center" }]
          : []),
        ...(usaCriterios
          ? [
              {
                text: fmtFraccionPct(r.criteriosCumplidos, r.criteriosTotales),
                alignment: "center",
              },
            ]
          : []),
      ];

      const columnas = [
        "Programa",
        "Proceso",
        "Ejecutado por",
        "Resultado",
        "Inicio",
        "Fin",
        "Duración",
        ...(usaPuntos ? ["Puntos"] : []),
        ...(usaPasos ? ["Pasos"] : []),
        ...(usaCriterios ? ["Criterios"] : []),
      ];
      const widths = [
        "*", "*", "*", 60, 50, 50, 38,
        ...(usaPuntos ? [40] : []),
        ...(usaPasos ? [58] : []),
        ...(usaCriterios ? [58] : []),
      ];

      const completados = registros.filter((r) => r.resultado === "completado");

      const semanas = Object.entries(resumen?.semanasMap ?? {}).sort(ordenarPorClave);

      descargarPdfCalidad({
        empresa,
        titulo: "Reporte de Calidad",
        subtitulo: "Registros de Procesos",
        extras: [`Total registros: ${registros.length}`],
        nombreArchivo: "Reporte_Calidad",
        kpis: [
          { label: "Total", value: resumen?.total ?? 0 },
          { label: "Completados", value: resumen?.completados ?? 0 },
          { label: "Con obs.", value: resumen?.conObservaciones ?? 0 },
          { label: "Rechazados", value: resumen?.rechazados ?? 0 },
          { label: "% Cumplimiento", value: `${resumen?.porcentajeCumplimiento ?? 0}%` },
          usaPuntos && { label: "% Puntos compl.", value: `${resumen?.porcentajePuntos ?? 0}%` },
          usaCriterios && { label: "% Criterios", value: `${resumen?.porcentajeCriterios ?? 0}%` },
          {
            label: "Dur. promedio",
            value:
              resumen?.duracionPromedioMinutos != null
                ? `${resumen.duracionPromedioMinutos} min`
                : "-",
          },
        ],
        preSecciones: barrasPdf(
          "Distribución por resultado",
          [
            { label: "Completado", val: resumen?.completados ?? 0, color: COLOR_RESULTADO.completado },
            { label: "Con obs.", val: resumen?.conObservaciones ?? 0, color: COLOR_RESULTADO.completado_con_observaciones },
            { label: "Rechazado", val: resumen?.rechazados ?? 0, color: COLOR_RESULTADO.rechazado },
            { label: "Pendiente", val: resumen?.pendientes ?? 0, color: COLOR_RESULTADO.pendiente },
          ],
          resumen?.total ?? 0
        ),
        secciones: [
          {
            titulo: "Registros por semana",
            columnas: ["Semana", "Completados", "Con obs.", "Rechazados", "Pendientes"],
            widths: ["auto", "*", "*", "*", "*"],
            filas: semanas.map(([sem, v]) => [
              fmtDiaCorto(sem),
              { text: `${v.completados}`, alignment: "center" },
              { text: `${v.conObs}`, alignment: "center" },
              { text: `${v.rechazados}`, alignment: "center" },
              { text: `${v.pendientes}`, alignment: "center" },
            ]),
          },
          {
            titulo: `Todos los registros (${registros.length})`,
            columnas,
            widths,
            filas: registros.map(filaRegistro),
          },
          {
            titulo: `Procesos Completados (${completados.length})`,
            columnas,
            widths,
            filas: completados.map(filaRegistro),
            vacio: "Sin procesos completados en el período.",
          },
        ],
      });
    } catch {
      message.error("No se pudo generar el PDF");
    } finally {
      this.setState({ loading: false });
    }
  }

  buildGraficas(resumen) {
    if (!resumen || resumen.total === 0) return { dataPie: [], dataBar: [], dataLine: [] };

    const dataPie = [
      { id: "Completado", label: "Completado", value: resumen.completados },
      { id: "Con obs.", label: "Con obs.", value: resumen.conObservaciones },
      { id: "Rechazado", label: "Rechazado", value: resumen.rechazados },
      { id: "Pendiente", label: "Pendiente", value: resumen.pendientes },
    ].filter((d) => d.value > 0);

    const dataBar = Object.entries(resumen.semanasMap ?? {})
      .sort(ordenarPorClave)
      .map(([sem, v]) => ({
        semana: fmtDiaCorto(sem),
        Completado: v.completados,
        "Con obs.": v.conObs,
        Rechazado: v.rechazados,
        Pendiente: v.pendientes,
      }));

    const puntosLine = Object.entries(resumen.diasMap ?? {})
      .sort(ordenarPorClave)
      .map(([clave, { suma, n }]) => ({ x: fmtDiaCorto(clave), y: Math.round(suma / n) }));

    return {
      dataPie,
      dataBar,
      dataLine: puntosLine.length ? [{ id: "% Cumplimiento", data: puntosLine }] : [],
    };
  }

  columnas() {
    const { resumen } = this.state;
    return [
      {
        title: "Programa",
        key: "programa",
        render: (_, r) => r.programa?.nombre ?? "-",
        ellipsis: true,
      },
      {
        title: "Proceso",
        key: "proceso",
        render: (_, r) => r.proceso?.nombre ?? "-",
        ellipsis: true,
      },
      {
        title: "Ejecutado por",
        key: "ejecutadoPor",
        render: (_, r) => fmtEjecutado(r),
        ellipsis: true,
      },
      {
        title: "Resultado",
        dataIndex: "resultado",
        render: (v) => <Tag color={RESULTADO_TAG[v] ?? "default"}>{RESULTADO_LABEL[v] ?? v}</Tag>,
        width: 140,
      },
      {
        title: "Inicio",
        dataIndex: "fechaInicio",
        render: fmtFecha,
        width: 120,
      },
      {
        title: "Fin",
        dataIndex: "fechaFin",
        render: fmtFecha,
        width: 120,
      },
      {
        title: "Duración",
        dataIndex: "duracionMinutos",
        render: fmtDuracion,
        align: "center",
        width: 90,
      },
      resumen?.usaPuntos && {
        title: "Puntos",
        key: "puntos",
        render: (_, r) => this.celdaFraccion(r.puntosCompletados, r.puntosTotales),
        align: "center",
        width: 110,
      },
      resumen?.usaPasos && {
        title: "Pasos",
        key: "pasos",
        render: (_, r) => this.celdaFraccion(r.pasosCompletados, r.pasosTotales),
        align: "center",
        width: 110,
      },
      resumen?.usaCriterios && {
        title: "Criterios ✓",
        key: "criterios",
        render: (_, r) => this.celdaFraccion(r.criteriosCumplidos, r.criteriosTotales),
        align: "center",
        width: 120,
      },
    ].filter(Boolean);
  }

  celdaFraccion(parte, total) {
    const pct = total > 0 ? Math.round((parte / total) * 100) : null;
    return (
      <span>
        {parte ?? 0}/{total ?? 0}
        {pct !== null && (
          <Tag
            color={pct === 100 ? "green" : pct >= 50 ? "orange" : "red"}
            style={{ marginLeft: 4 }}
          >
            {pct}%
          </Tag>
        )}
      </span>
    );
  }

  kpis() {
    const { resumen } = this.state;
    if (!resumen) return [];
    return [
      { key: "total", label: "Total", value: resumen.total, color: "#0f172a" },
      { key: "completados", label: "Completados", value: resumen.completados, color: COLOR_RESULTADO.completado },
      { key: "conObs", label: "Con obs.", value: resumen.conObservaciones, color: COLOR_RESULTADO.completado_con_observaciones },
      { key: "rechazados", label: "Rechazados", value: resumen.rechazados, color: COLOR_RESULTADO.rechazado },
      { key: "cumplimiento", label: "% Cumplimiento", value: `${resumen.porcentajeCumplimiento}%`, color: "#00BFD6" },
      resumen.usaPuntos && {
        key: "puntos",
        label: "% Puntos compl.",
        value: `${resumen.porcentajePuntos}%`,
        color: "#0ea5e9",
        hint: `${resumen.totalPuntosCompletados}/${resumen.totalPuntos} puntos`,
      },
      resumen.usaCriterios && {
        key: "criterios",
        label: "% Criterios",
        value: `${resumen.porcentajeCriterios}%`,
        color: "#6366f1",
        hint: `${resumen.totalCriteriosCumplidos}/${resumen.totalCriterios} criterios`,
      },
      resumen.usaPasos && {
        key: "pasos",
        label: "% Pasos",
        value: `${resumen.porcentajePasos}%`,
        color: "#a78bfa",
        hint: `${resumen.totalPasosCompletados}/${resumen.totalPasos} pasos`,
      },
      resumen.duracionPromedioMinutos != null && {
        key: "duracion",
        label: "Duración prom.",
        value: `${resumen.duracionPromedioMinutos} min`,
        color: "#8b5cf6",
      },
    ].filter(Boolean);
  }

  graficas() {
    const { resumen } = this.state;
    if (!resumen || resumen.total === 0) return null;

    const { dataPie, dataBar, dataLine } = this.buildGraficas(resumen);
    const movil = isMobile();

    return (
      <>
        <Row gutter={[14, 14]} style={{ marginBottom: 14 }}>
          {dataPie.length > 0 && (
            <Col xs={24} md={9}>
              <ChartCard
                label="Resultados"
                titulo="Distribución por resultado"
                pill={`${resumen.total} registros`}
                delay={0.06}
                altura={250}
              >
                <ResponsivePie
                  data={dataPie}
                  margin={
                    movil
                      ? { top: 10, right: 10, bottom: 40, left: 10 }
                      : { top: 10, right: 20, bottom: 46, left: 20 }
                  }
                  colors={COLORES_RESULTADO}
                  innerRadius={0.67}
                  padAngle={1.8}
                  cornerRadius={6}
                  activeOuterRadiusOffset={9}
                  borderWidth={0}
                  enableArcLabels={false}
                  arcLinkLabelsSkipAngle={movil ? 360 : 14}
                  arcLinkLabelsTextColor="#94a3b8"
                  arcLinkLabelsThickness={1}
                  arcLinkLabelsColor={{ from: "color" }}
                  theme={NIVO_THEME}
                  legends={[
                    {
                      anchor: "bottom",
                      direction: "row",
                      translateY: 42,
                      itemsSpacing: 6,
                      itemWidth: movil ? 68 : 82,
                      itemHeight: 16,
                      itemTextColor: "#64748b",
                      symbolSize: 9,
                      symbolShape: "circle",
                      effects: [{ on: "hover", style: { itemTextColor: "#0f172a" } }],
                    },
                  ]}
                  tooltip={({ datum }) => (
                    <TooltipCalidad
                      color={datum.color}
                      titulo={datum.label}
                      filas={[
                        { label: "Registros", value: datum.value },
                        {
                          label: "Del total",
                          value: `${Math.round((datum.value / resumen.total) * 100)}%`,
                        },
                      ]}
                    />
                  )}
                />
              </ChartCard>
            </Col>
          )}

          {dataBar.length > 0 && (
            <Col xs={24} md={dataPie.length ? 15 : 24}>
              <ChartCard
                label="Tendencia"
                titulo="Registros por semana"
                accent="linear-gradient(90deg, #10b981, #34d399)"
                delay={0.12}
                altura={250}
              >
                <ResponsiveBar
                  data={dataBar}
                  keys={["Completado", "Con obs.", "Rechazado", "Pendiente"]}
                  indexBy="semana"
                  margin={{ top: 10, right: 8, bottom: movil ? 66 : 58, left: 40 }}
                  padding={0.28}
                  innerPadding={2}
                  groupMode="stacked"
                  colors={COLORES_RESULTADO}
                  colorBy="id"
                  borderRadius={4}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{ tickSize: 0, tickPadding: 10, tickRotation: -30 }}
                  axisLeft={{ tickSize: 0, tickPadding: 8 }}
                  enableGridX={false}
                  gridYValues={4}
                  enableLabel={false}
                  theme={NIVO_THEME}
                  tooltip={({ id, value, indexValue, color }) => (
                    <TooltipCalidad
                      color={color}
                      titulo={`Semana del ${indexValue}`}
                      filas={[{ label: id, value }]}
                    />
                  )}
                  legends={
                    movil
                      ? []
                      : [
                          {
                            dataFrom: "keys",
                            anchor: "bottom",
                            direction: "row",
                            translateY: 52,
                            itemWidth: 96,
                            itemHeight: 16,
                            itemsSpacing: 4,
                            symbolSize: 8,
                            symbolShape: "circle",
                            itemTextColor: "#64748b",
                            effects: [{ on: "hover", style: { itemTextColor: "#1e293b" } }],
                          },
                        ]
                  }
                />
              </ChartCard>
            </Col>
          )}
        </Row>

        {dataLine.length > 0 && dataLine[0].data.length > 1 && (
          <Row style={{ marginBottom: 14 }}>
            <Col span={24}>
              <ChartCard
                label="Evolución"
                titulo="% de cumplimiento por día"
                accent="linear-gradient(90deg, #6366f1, #a78bfa)"
                delay={0.18}
                altura={210}
              >
                <ResponsiveLine
                  data={dataLine}
                  margin={{ top: 10, right: 20, bottom: 54, left: 46 }}
                  xScale={{ type: "point" }}
                  yScale={{ type: "linear", min: 0, max: 100 }}
                  curve="monotoneX"
                  colors={["#6366f1"]}
                  lineWidth={2.5}
                  pointSize={6}
                  pointColor="#ffffff"
                  pointBorderWidth={2}
                  pointBorderColor={{ from: "serieColor" }}
                  enableArea
                  areaOpacity={0.08}
                  enableSlices="x"
                  enableGridX={false}
                  gridYValues={[0, 25, 50, 75, 100]}
                  theme={NIVO_THEME}
                  axisBottom={{
                    tickSize: 0,
                    tickPadding: 10,
                    tickRotation: -30,
                    tickValues: dataLine[0].data
                      .map((d) => d.x)
                      .filter(
                        (_, i) => i % Math.ceil(dataLine[0].data.length / (movil ? 6 : 12)) === 0
                      ),
                  }}
                  axisLeft={{ tickSize: 0, tickPadding: 8, format: (v) => `${v}%` }}
                  sliceTooltip={({ slice }) => (
                    <TooltipCalidad
                      color="#6366f1"
                      titulo={slice.points[0]?.data.x}
                      filas={[{ label: "Cumplimiento", value: `${slice.points[0]?.data.y}%` }]}
                    />
                  )}
                />
              </ChartCard>
            </Col>
          </Row>
        )}
      </>
    );
  }

  render() {
    const { registros, totalRegistros, resumen, loading } = this.state;

    return (
      <Form
        ref={this.formRef}
        name="reporteCalidadGeneral"
        onFinish={this.consultarReporte.bind(this)}
        onFinishFailed={ErrorItem}
        initialValues={PERIODO_INICIAL}
      >
        <Spin spinning={loading} wrapperClassName="cal-spin">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <Space>
              {resumen && (
                <ButtonItem verde onClick={() => this.descargarExcel()} icon={<FileExcelOutlined />}>
                  Excel
                </ButtonItem>
              )}
              {resumen && (
                <ButtonItem rojo onClick={() => this.descargarPdf()} icon={<FilePdfOutlined />}>
                  PDF
                </ButtonItem>
              )}
              <ButtonItem type="primary" htmlType="submit">
                Consultar
              </ButtonItem>
            </Space>
          </div>

          <FiltrosCalidad
            campos={["periodo", "programa", "proceso", "colaborador", "resultado"]}
            formRef={this.formRef}
          />

          <KpiRow items={this.kpis()} />

          {this.graficas()}

          <TableItemDinamico
            onRef={(ref) => (this.tableRef = ref)}
            totalRecords={totalRegistros}
            onFetchData={this.onFetchData.bind(this)}
            loading={loading}
            size="small"
            dataSource={registros}
            columns={this.columnas()}
            rowKey={(r) => r._id?.toString()}
          />
        </Spin>
      </Form>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ReporteGeneral);
